import type { MaybeRefOrGetter } from 'vue';
import { readonly, ref, toValue } from 'vue';

const OVERFLOW_TOLERANCE_PX = 1;
const HIDDEN_BOX_MAX_PX = 1;
const CLIPPING_OVERFLOW_VALUES = new Set(['hidden', 'clip', 'auto', 'scroll']);

/**
 * Measures, each time `measure()` runs, whether `target` clips its content horizontally, and returns the result.
 *
 * Descendants count only when they clip themselves: a shrunken `overflow-hidden` flex item or a nested `truncate` hides
 * content without widening `target`'s scroll width.
 */
export function useTruncation(target: MaybeRefOrGetter<HTMLElement | null | undefined>) {
	const isTruncated = ref(false);

	function measure(): boolean {
		const element = toValue(target);
		isTruncated.value = element ? isClipped(element) : false;
		return isTruncated.value;
	}

	return {
		isTruncated: readonly(isTruncated),
		measure,
	};
}

function isClipped(root: HTMLElement): boolean {
	if (overflowsHorizontally(root)) return true;

	for (const element of root.querySelectorAll('*')) {
		if (overflowsHorizontally(element) && !isHiddenBox(element) && clipsOverflowX(element)) return true;
	}

	return false;
}

// Boxes of at most 1px each way are hidden rather than clipped: `display: none` (e.g. kept mounted by `v-show`), inline
// boxes, which ignore `overflow`, and `sr-only` text. A flex item squeezed to 0px wide keeps its height and still clips.
function isHiddenBox(element: Element): boolean {
	return element.clientWidth <= HIDDEN_BOX_MAX_PX && element.clientHeight <= HIDDEN_BOX_MAX_PX;
}

function overflowsHorizontally(element: Element): boolean {
	return element.scrollWidth - element.clientWidth > OVERFLOW_TOLERANCE_PX;
}

function clipsOverflowX(element: Element): boolean {
	return CLIPPING_OVERFLOW_VALUES.has(getComputedStyle(element).overflowX);
}
